import { useCallback, useEffect, useRef, useState } from "react";
import type { BuildLocation, BuildSelection, CardDef, CardInstance, CardParams, Command, GameView, PlayerView, Resource, ResourcePlan } from "../types";
import { phaseLabels, RESOURCES, resourceMeta } from "../gameMeta";
import { GameCard } from "./Card";
import { allPlaced } from "./Board";
import { useTargetBoardPicker } from "./BoardInteraction";
import { DeckDirectionGuide } from "./DeckDirection";
import {
  ResourcePlanDialog,
  automaticPlan,
  resourceChoiceExists,
  resourceLocations,
  resourceTotal,
  type ResourcePlanConfirmation,
  type ResourcePlanRequest,
} from "./ResourcePlanner";
import { ResourceAmounts, ResourceBadge, ResourceCountSelector } from "./ResourceIcon";

interface Props {
  game: GameView;
  definitions: Map<string, CardDef>;
  busy: boolean;
  selectedRegions: number[];
  onClearRegions: () => void;
  onCommand: (command: Command) => void;
  selectedBuild: BuildSelection | null;
  onSelectBuild: (selection: BuildSelection | null) => void;
  boardCommand?: { id: number; command: Command } | null;
  onBoardCommandHandled?: (id: number) => void;
}

type CardDialog = { instance: CardInstance; open: boolean } | null;
type DirectCommandConfirmation = ResourcePlanConfirmation & { danger?: boolean };
type PlannedCommand = {
  command: Command;
  request: ResourcePlanRequest;
  confirmation?: DirectCommandConfirmation;
  version: number;
};
type PendingConfirmation = {
  command: Command;
  copy: DirectCommandConfirmation;
  version: number;
};

export function ActionPanel({
  game,
  definitions,
  busy,
  selectedRegions,
  onClearRegions,
  onCommand,
  selectedBuild,
  onSelectBuild,
  boardCommand,
  onBoardCommandHandled,
}: Props) {
  const own = game.players[game.viewer === "rood" ? 0 : 1];
  const opponent = game.players[game.viewer === "rood" ? 1 : 0];
  const isTurn = game.turn.active === game.viewer;
  const [dialog, setDialog] = useState<CardDialog>(null);
  const [tradeGive, setTradeGive] = useState<Resource>("hout");
  const [tradeReceive, setTradeReceive] = useState<Resource>("baksteen");
  const [tradeRate, setTradeRate] = useState(3);
  const [tradeRegion, setTradeRegion] = useState<number>();
  const [swapCard, setSwapCard] = useState<number>();
  const [swapFromDeck, setSwapFromDeck] = useState<number>();
  const [swapToDeck, setSwapToDeck] = useState<number>();
  const [searchPayment, setSearchPayment] = useState<Resource[]>([]);
  const [planned, setPlanned] = useState<PlannedCommand | null>(null);
  const [confirmation, setConfirmation] = useState<PendingConfirmation | null>(null);
  const phase = game.turn.phase;
  const scoutVillageRequired = Boolean(game.turn.scout_village_required);
  const hand = own.hand || [];
  const placedIds = allPlaced(own).map((placed) => placed.instance.card_id);
  const largeTradeRegions = largeTradeShipRegionIds(own);
  const regularShipForTrade = placedIds.includes(`${tradeGive === "hout" ? "base.hout" : tradeGive === "baksteen" ? "base.baksteen" : tradeGive === "graan" ? "base.graan" : tradeGive === "wol" ? "base.wol" : tradeGive === "erts" ? "base.erts" : "base.goud"}handelsschip`);
  const largeShipForTrade = [...own.principality.upper_regions, ...own.principality.lower_regions]
    .some((region) => largeTradeRegions.has(region.region_id) && region.resource === tradeGive && region.amount >= 2);
  const selectedLargeShipRegion = [...own.principality.upper_regions, ...own.principality.lower_regions]
    .some((region) => region.region_id === tradeRegion && largeTradeRegions.has(region.region_id) && region.resource === tradeGive && region.amount >= 2);
  const unusedMints = placedIds.filter((id) => id === "gold.muntenhandel").length - (game.turn.once_per_turn.muntenhandel || 0);
  const selectedBuildDefinition = selectedBuild ? definitions.get(selectedBuild.instance.card_id) : undefined;
  const selectedBuildOption = selectedBuild ? game.build_options?.cards.find((option) => (
    option.instance_id === selectedBuild.instance.instance_id
    && option.from_open === (selectedBuild.source === "open")
  )) : undefined;
  const selectedDiscounts = selectedBuildOption?.discount_resources
    || (selectedBuildDefinition && buildDiscountAllowed(own, selectedBuildDefinition)
      ? RESOURCES.filter((resource, index) => selectedBuildDefinition.cost[index] > 0)
      : []);
  const tradeRateAllowed = tradeRate === 3
    || (tradeRate === 2 && (regularShipForTrade || selectedLargeShipRegion))
    || (tradeRate === 1 && tradeGive === "goud" && unusedMints > 0);

  const executeCommand = useCallback((command: Command, plan?: ResourcePlan) => {
    if (command.type === "build_card" || command.type === "build_open_card") onSelectBuild(null);
    onCommand(plan ? { type: "with_resource_plan", plan, command } : command);
  }, [onCommand, onSelectBuild]);
  const requestCommand = useCallback((command: Command) => {
    const request = commandResourcePlan(command, game, own, definitions);
    const copy = directCommandConfirmation(command, own, game, definitions);
    if (!request) {
      if (copy) setConfirmation({ command, copy, version: game.version });
      else executeCommand(command);
      return;
    }
    const plan = automaticPlan(own, request);
    const incomplete = plan.payment.length !== request.payment.length
      || (request.requireStorage && plan.storage.length !== request.storage.length);
    const hasChoice = resourceChoiceExists(own, request.payment, "payment")
      || resourceChoiceExists(own, request.storage, "storage");
    if (copy || incomplete || hasChoice) {
      setPlanned({ command, request, confirmation: copy || undefined, version: game.version });
    } else {
      executeCommand(command, plan);
    }
  }, [definitions, executeCommand, game, own]);
  const handledBoardCommand = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!boardCommand || handledBoardCommand.current === boardCommand.id) return;
    handledBoardCommand.current = boardCommand.id;
    requestCommand(boardCommand.command);
    onBoardCommandHandled?.(boardCommand.id);
  }, [boardCommand, onBoardCommandHandled, requestCommand]);
  useEffect(() => {
    setPlanned((current) => current && current.version !== game.version ? null : current);
    setConfirmation((current) => current && current.version !== game.version ? null : current);
  }, [game.version]);
  useEffect(() => {
    setSwapCard(undefined);
    setSwapFromDeck(undefined);
    setSwapToDeck(undefined);
    setSearchPayment([]);
  }, [game.turn.number]);
  const submit = (command: Command) => {
    requestCommand(command);
    setDialog(null);
  };

  return (
    <aside className="action-panel">
      <header>
        <span className="eyebrow">Beurt {game.turn.number || "—"}</span>
        <h2>{phaseLabels[phase]}</h2>
        <p>{turnHint(game, isTurn)}</p>
      </header>

      {selectedBuild && selectedBuildDefinition ? (
        <section className="placement-toolbar" aria-live="polite">
          <div><span className="eyebrow">Kaart geselecteerd</span><strong>{selectedBuildDefinition.name}</strong></div>
          <p>Klik nu rechtstreeks op een goud oplichtende bouwplaats op jouw bord.</p>
          {selectedDiscounts.length ? (
            <div className="discount-options" aria-label="Bouwkorting">
              <small>Korting:</small>
              <button disabled={selectedBuildOption ? !selectedBuildOption.can_build_without_discount : false} className={!selectedBuild.discount_resource ? "selected" : ""} onClick={() => onSelectBuild({ ...selectedBuild, discount_resource: undefined })}>geen</button>
              {selectedDiscounts.map((resource) => (
                <button
                  key={resource}
                  className={selectedBuild.discount_resource === resource ? "selected" : ""}
                  aria-label={`1 ${resourceMeta[resource].label} korting`}
                  onClick={() => onSelectBuild({ ...selectedBuild, discount_resource: resource })}
                ><ResourceBadge resource={resource} amount={1} compact quantityPrefix="−" /></button>
              ))}
            </div>
          ) : null}
          <button className="cancel-placement" onClick={() => onSelectBuild(null)}>Selectie annuleren</button>
        </section>
      ) : null}

      {game.status === "gepauzeerd" ? (
        <div className="pause-card">
          <span>Ⅱ</span>
          <strong>Het spel is gepauzeerd</strong>
          <p>{game.pause_reason || "Jullie kunnen verder wanneer het uitkomt."}</p>
          <button className="primary" disabled={busy} onClick={() => onCommand({ type: "resume" })}>
            Spel hervatten
          </button>
        </div>
      ) : null}

      {game.status === "voorbereiding" && phase === "landschappen_ordenen" ? (
        <section className="panel-block">
          <h3>Jouw landschappen</h3>
          <p>Selecteer twee landschappen op jouw bord om hun plek te wisselen. Rond af zodra de getallen goed liggen.</p>
          <div className="button-row">
            {selectedRegions.length ? (
              <button type="button" className="selection-clear" disabled={busy} onClick={onClearRegions}>
                Selectie wissen
              </button>
            ) : null}
            <button
              disabled={selectedRegions.length !== 2 || busy}
              onClick={() => {
                onCommand({
                  type: "swap_regions",
                  first_region_id: selectedRegions[0],
                  second_region_id: selectedRegions[1],
                });
                onClearRegions();
              }}
            >
              Verwissel selectie
            </button>
            <button className="primary" disabled={own.ready || busy} onClick={() => onCommand({ type: "ready" })}>
              {own.ready ? "Klaar — wacht op de ander" : "Mijn opstelling is klaar"}
            </button>
          </div>
        </section>
      ) : null}

      {game.status === "actief" && isTurn && phase === "wachten_op_worp" ? (
        <button className="roll-button" disabled={busy} onClick={() => onCommand({ type: "roll" })}>
          <span className="dice">⚄</span>
          <span><strong>Dobbelstenen werpen</strong><small>Opbrengst + gebeurtenis</small></span>
        </button>
      ) : null}

      {game.status === "actief" && isTurn && phase === "actiefase" ? (
        <>
          {scoutVillageRequired ? (
            <section className="panel-block scout-village-required" role="status">
              <span className="eyebrow">Verkenner is gespeeld</span>
              <h3>Bouw nu het verplichte dorp</h3>
              <p>Klik op het halftransparante dorp links of rechts op je bord. Na de betaling kies je uit de volledige landschapsstapel expliciet wat boven en wat onder het nieuwe dorp komt.</p>
              <p className="success-note">De kaart en deze tussenstand zijn opgeslagen. Tot de dorpbouw klaar is, zijn andere spelacties vergrendeld.</p>
            </section>
          ) : (
          <>
          {game.turn.built_settlement_region_ids.length === 2 ? (
            <section className="panel-block new-landscapes">
              <h3>Nieuwe landschappen plaatsen</h3>
              <p>Selecteer de twee nieuwe landschappen op je bord en verwissel ze desgewenst. Verkenner moet vóór een volgende dorpbouw worden gespeeld; Bob kan nog wel direct worden gebruikt.</p>
              <button
                disabled={
                  busy
                  || selectedRegions.length !== 2
                  || !selectedRegions.every((id) => game.turn.built_settlement_region_ids.includes(id))
                }
                onClick={() => {
                  onCommand({
                    type: "swap_regions",
                    first_region_id: selectedRegions[0],
                    second_region_id: selectedRegions[1],
                  });
                  onClearRegions();
                }}
              >
                Boven en onder verwisselen
              </button>
              {selectedRegions.length ? (
                <button type="button" className="selection-clear" disabled={busy} onClick={onClearRegions}>
                  Selectie wissen
                </button>
              ) : null}
            </section>
          ) : null}
          <section className="panel-block build-controls">
            <h3>Bouwen doe je op het bord</h3>
            <p>Klik op de halftransparante straat, het dorp of de stadsmarkering op jouw vorstendom. Kies voor een uitbouw eerst een kaart uit je hand.</p>
          </section>
          <section className="panel-block trade-controls">
            <h3>Ruilen met de voorraad</h3>
            <div className="inline-fields">
              <select value={tradeRate} onChange={(event) => setTradeRate(Number(event.target.value))} aria-label="Aantal te geven">
                <option value={3}>3 voor 1</option><option value={2} disabled={!regularShipForTrade && !largeShipForTrade}>2 voor 1 (schip)</option><option value={1} disabled={tradeGive !== "goud" || unusedMints <= 0}>1 voor 1 (Muntenhandel)</option>
              </select>
              <select value={tradeGive} onChange={(event) => setTradeGive(event.target.value as Resource)} aria-label="Geef">
                {RESOURCES.map((resource) => <option key={resource} value={resource}>Geef {resourceMeta[resource].label}</option>)}
              </select>
              <select value={tradeReceive} onChange={(event) => setTradeReceive(event.target.value as Resource)} aria-label="Ontvang">
                {RESOURCES.map((resource) => <option key={resource} value={resource}>Neem {resourceMeta[resource].label}</option>)}
              </select>
              {tradeRate === 2 ? <select value={tradeRegion || ""} onChange={(event) => setTradeRegion(Number(event.target.value) || undefined)} aria-label="Landschap voor Groot handelsschip"><option value="">Gewoon handelsschip</option>{[...own.principality.upper_regions, ...own.principality.lower_regions].filter((region) => largeTradeRegions.has(region.region_id) && region.resource === tradeGive && region.amount >= 2).map((region) => <option key={region.region_id} value={region.region_id}>{resourceMeta[region.resource].label} {region.production_number} (Groot schip)</option>)}</select> : null}
              <div className="trade-preview" aria-label={`Ruil ${tradeRate} ${resourceMeta[tradeGive].label} voor 1 ${resourceMeta[tradeReceive].label}`}>
                <span>Geef</span><ResourceBadge resource={tradeGive} amount={tradeRate} />
                <span>voor</span><ResourceBadge resource={tradeReceive} amount={1} />
              </div>
              <button disabled={busy || tradeGive === tradeReceive || !tradeRateAllowed} onClick={() => requestCommand({ type: "trade", give: tradeGive, receive: tradeReceive, count: tradeRate, source_region_id: tradeRegion })}>Ruil deze grondstoffen</button>
            </div>
          </section>
          {allPlaced(own).some((placed) => {
            const kind = definitions.get(placed.instance.card_id)?.kind;
            return kind === "nederzettingsgebouw" || kind === "nederzettingseenheid" || kind === "stadsgebouw" || kind === "landschapsuitbouw";
          }) ? (
            <section className="panel-block demolition-controls">
              <h3>Uitbouw verwijderen</h3>
              <p>Gebouwen, eenheden en bijzondere locaties mogen kosteloos naar de aflegstapel. Open themakaarten keren terug naar de open voorraad. Na je keuze verschijnt altijd eerst een apart bevestigingsscherm.</p>
              <div className="return-card-row demolition-list">
                {allPlaced(own).flatMap((placed) => {
                  const definition = definitions.get(placed.instance.card_id);
                  if (!definition || !["nederzettingsgebouw", "nederzettingseenheid", "stadsgebouw", "landschapsuitbouw"].includes(definition.kind)) return [];
                  return [
                    <button
                      type="button"
                      className="danger"
                      disabled={busy}
                      key={placed.instance.instance_id}
                      aria-haspopup="dialog"
                      onClick={() => requestCommand({ type: "remove_own_card", instance_id: placed.instance.instance_id })}
                    >
                      {definition.name} verwijderen…
                    </button>,
                  ];
                })}
              </div>
            </section>
          ) : null}
          <button className="primary wide" disabled={busy} onClick={() => onCommand({ type: "end_action_phase" })}>
            Actiefase afronden
          </button>
          </>
          )}
        </>
      ) : null}

      {game.status === "actief" && isTurn && phase === "hand_aanvullen" ? (
        <section className="panel-block">
          <h3>Hand aanvullen</h3>
          <p>Kies een stapel. Heb je te veel kaarten, leg er dan één terug onder een passende stapel.</p>
          <DeckDirectionGuide compact />
          <div className="deck-buttons">
            {game.supply.decks.map((deck) => (
              <button disabled={busy || !deck.count} key={deck.deck_id} onClick={() => onCommand({ type: "draw_from_deck", deck_id: deck.deck_id })}>
                <span>{deck.label}</span><small>{deck.count} kaarten · trek bovenop</small>
              </button>
            ))}
          </div>
          {hand.map((card) => {
            const def = definitions.get(card.card_id);
            const decks = game.supply.decks.filter((deck) => deck.set === def?.set);
            return (
              <div className="return-card-row" key={card.instance_id}>
                <span>{def?.name}</span>
                {decks.map((deck) => (
                  <button key={deck.deck_id} disabled={busy} onClick={() => onCommand({ type: "put_card_under_deck", instance_id: card.instance_id, deck_id: deck.deck_id })}>
                    Onderop stapel {deck.deck_id}
                  </button>
                ))}
              </div>
            );
          })}
        </section>
      ) : null}

      {game.status === "actief" && isTurn && phase === "kaart_ruilen" ? (
        <section className="panel-block">
          <h3>Optioneel één handkaart ruilen</h3>
          <DeckDirectionGuide compact />
          <div className="inline-fields swap-fields">
            <select disabled={busy || own.hand_swap_used} value={swapCard || ""} onChange={(event) => {
              const next = Number(event.target.value) || undefined;
              setSwapCard(next);
              setSwapFromDeck(undefined);
              setSearchPayment([]);
              if (!next) setSwapToDeck(undefined);
            }} aria-label="Kaart om te ruilen">
              <option value="">Kies handkaart</option>
              {hand.map((card) => <option key={card.instance_id} value={card.instance_id}>{definitions.get(card.card_id)?.name}</option>)}
            </select>
            <select disabled={busy || own.hand_swap_used} value={swapFromDeck || ""} onChange={(event) => setSwapFromDeck(Number(event.target.value))} aria-label="Stapel om kaart onder te leggen">
              <option value="">Leg onderop in…</option>
              {game.supply.decks.filter((deck) => deck.set === definitions.get(hand.find((card) => card.instance_id === swapCard)?.card_id || "")?.set).map((deck) => <option key={deck.deck_id} value={deck.deck_id}>{deck.label} · onderop</option>)}
            </select>
            <select disabled={busy || own.hand_swap_used} value={swapToDeck || ""} onChange={(event) => setSwapToDeck(Number(event.target.value))} aria-label="Stapel om van te trekken">
              <option value="">Trek bovenop van…</option>
              {game.supply.decks.map((deck) => <option key={deck.deck_id} value={deck.deck_id}>{deck.label} · bovenop</option>)}
            </select>
            <button disabled={!swapCard || !swapFromDeck || !swapToDeck || busy || own.hand_swap_used} onClick={() => onCommand({ type: "swap_hand_card", instance_id: swapCard!, put_under_deck_id: swapFromDeck!, draw_deck_id: swapToDeck!, search_instance_id: null })}>Ruil bovenste kaart</button>
            {swapCard || swapFromDeck || swapToDeck || searchPayment.length ? (
              <button
                type="button"
                className="selection-clear"
                disabled={busy || own.hand_swap_used}
                onClick={() => {
                  setSwapCard(undefined);
                  setSwapFromDeck(undefined);
                  setSwapToDeck(undefined);
                  setSearchPayment([]);
                }}
              >Ruil-/zoekselectie wissen</button>
            ) : null}
          </div>
          {own.hand_swap_used ? <p className="success-note swap-used-note" role="status">Je hebt deze beurt al een handkaart geruild. Nogmaals ruilen of gericht zoeken is niet toegestaan.</p> : null}
          {swapCard && swapFromDeck && swapToDeck ? <DirectedSearchPayment own={own} selected={searchPayment} setSelected={setSearchPayment} onSearch={() => requestCommand({ type: "begin_search_swap", instance_id: swapCard, put_under_deck_id: swapFromDeck, draw_deck_id: swapToDeck, payment: searchPayment })} busy={busy} disabled={own.hand_swap_used} /> : null}
          <button className="primary wide" disabled={busy} onClick={() => onCommand({ type: "finish_turn" })}>Beurt beëindigen</button>
        </section>
      ) : null}

      <section className="panel-block hand-block">
        <div className="section-heading"><h3>Jouw hand</h3><span>{hand.length}</span></div>
        <div className="hand-cards">
          {hand.length ? hand.map((instance) => {
            const def = definitions.get(instance.card_id);
            const buildableKind = def && ["nederzettingsgebouw", "nederzettingseenheid", "stadsgebouw", "landschapsuitbouw"].includes(def.kind);
            const legalBuild = game.build_options?.cards.find((option) => option.instance_id === instance.instance_id && !option.from_open);
            const scoutCanStart = instance.card_id !== "base.verkenner" || Boolean(game.build_options?.village_sides.length);
            const usableNow = (phase === "actiefase" && ((def?.kind === "actie" && !scoutVillageRequired && scoutCanStart) || (buildableKind && (!game.build_options || legalBuild))))
              || (phase === "wachten_op_worp"
                && def?.kind === "actie"
                && ["base.marileen", "progress.marileen", "gold.reiner"].includes(instance.card_id));
            const chooseCard = () => {
              if (def?.kind === "actie") {
                onSelectBuild(null);
                setDialog({ instance, open: false });
              } else if (buildableKind) {
                setDialog(null);
                onSelectBuild(selectedBuild?.instance.instance_id === instance.instance_id ? null : {
                  instance,
                  source: "hand",
                  discount_resource: legalBuild && !legalBuild.can_build_without_discount ? legalBuild.discount_resources[0] : undefined,
                });
              }
            };
            return (
              <GameCard
                key={instance.instance_id}
                instance={instance}
                definition={def}
                selected={selectedBuild?.instance.instance_id === instance.instance_id}
                disabled={Boolean(game.status === "actief" && isTurn && phase === "actiefase" && ((buildableKind && game.build_options && !legalBuild) || (def?.kind === "actie" && (scoutVillageRequired || !scoutCanStart))))}
                onClick={game.status === "actief" && isTurn && usableNow && (def?.kind === "actie" || buildableKind) ? chooseCard : undefined}
                footer={game.status === "actief" && isTurn && usableNow && (def?.kind === "actie" || buildableKind) ? (
                  <button onClick={(event) => { event.stopPropagation(); chooseCard(); }}>
                    {def?.kind === "actie" ? "Spelen" : selectedBuild?.instance.instance_id === instance.instance_id ? "Geselecteerd ✓" : "Op bord plaatsen"}
                  </button>
                ) : undefined}
              />
            );
          }) : <p className="empty-note">Geen kaarten in je hand.</p>}
        </div>
      </section>

      {game.supply.open_cards.length ? (
        <section className="panel-block">
          <div className="section-heading"><h3>Open themakaarten</h3><span>{game.supply.open_cards.length}</span></div>
          <div className="hand-cards open-cards">
            {game.supply.open_cards.map((instance) => {
              const definition = definitions.get(instance.card_id);
              const legalBuild = game.build_options?.cards.find((option) => option.instance_id === instance.instance_id && option.from_open);
              const usable = game.status === "actief" && isTurn && phase === "actiefase" && (!game.build_options || legalBuild);
              const chooseCard = () => {
                setDialog(null);
                onSelectBuild(selectedBuild?.instance.instance_id === instance.instance_id ? null : {
                  instance,
                  source: "open",
                  discount_resource: legalBuild && !legalBuild.can_build_without_discount ? legalBuild.discount_resources[0] : undefined,
                });
              };
              return <GameCard
                key={instance.instance_id}
                instance={instance}
                definition={definition}
                selected={selectedBuild?.instance.instance_id === instance.instance_id}
                disabled={Boolean(game.status === "actief" && isTurn && phase === "actiefase" && game.build_options && !legalBuild)}
                onClick={usable ? chooseCard : undefined}
                footer={usable ? (
                  <button onClick={(event) => { event.stopPropagation(); chooseCard(); }}>{selectedBuild?.instance.instance_id === instance.instance_id ? "Geselecteerd ✓" : "Op bord plaatsen"}</button>
                ) : undefined}
              />;
            })}
          </div>
        </section>
      ) : null}

      {dialog && definitions.get(dialog.instance.card_id)?.kind === "actie" ? (
        <ActionCardDialog game={game} own={own} opponent={opponent} instance={dialog.instance} definition={definitions.get(dialog.instance.card_id)!} definitions={definitions} busy={busy} onCancel={() => setDialog(null)} onSubmit={(params) => submit({ type: "play_action", instance_id: dialog.instance.instance_id, params })} />
      ) : null}
      {planned ? (
        <ResourcePlanDialog
          key={JSON.stringify(planned.command)}
          player={own}
          request={planned.request}
          confirmation={planned.confirmation}
          busy={busy}
          onCancel={() => setPlanned(null)}
          onConfirm={(plan) => {
            const command = planned.command;
            setPlanned(null);
            executeCommand(command, plan);
          }}
        />
      ) : null}
      {confirmation ? (
        <CommandConfirmationDialog
          copy={confirmation.copy}
          busy={busy}
          onCancel={() => setConfirmation(null)}
          onConfirm={() => {
            const command = confirmation.command;
            setConfirmation(null);
            executeCommand(command);
          }}
        />
      ) : null}
    </aside>
  );
}

export function directCommandNeedsConfirmation(command: Command): boolean {
  if (command.type === "with_resource_plan") return directCommandNeedsConfirmation(command.command);
  return command.type === "build_road"
    || command.type === "build_village"
    || command.type === "upgrade_city"
    || command.type === "build_card"
    || command.type === "build_open_card"
    || command.type === "remove_own_card";
}

function directCommandConfirmation(
  command: Command,
  own: PlayerView,
  game: GameView,
  definitions: Map<string, CardDef>,
): DirectCommandConfirmation | null {
  if (command.type === "with_resource_plan") return directCommandConfirmation(command.command, own, game, definitions);
  if (!directCommandNeedsConfirmation(command)) return null;
  if (command.type === "build_road") {
    const side = command.side === "links" ? "linkerkant" : "rechterkant";
    return {
      eyebrow: "Bouwactie bevestigen",
      title: "Straat bouwen?",
      description: `Je bouwt een straat aan de ${side} van je vorstendom. Controleer de plek en betaling voordat je bouwt.`,
      confirmLabel: "Straat bouwen",
    };
  }
  if (command.type === "build_village") {
    const side = command.side === "links" ? "linkerkant" : "rechterkant";
    if (game.turn.scout_village_required) {
      return {
        eyebrow: "Verkenner · verplichte dorpbouw",
        title: `Dorp aan de ${side} bouwen?`,
        description: `Je rondt Verkenner af met een dorp aan de ${side}. Na de betaling kies je twee landschappen uit de volledige stapel en leg je ze expliciet boven en onder.`,
        confirmLabel: "Dorp bouwen en landschappen kiezen",
      };
    }
    return {
      eyebrow: "Bouwactie bevestigen",
      title: "Dorp bouwen?",
      description: `Je breidt je vorstendom aan de ${side} uit met een dorp en twee nieuwe landschappen. Controleer dit voordat je bouwt.`,
      confirmLabel: "Dorp bouwen",
    };
  }
  if (command.type === "upgrade_city") {
    const townIndex = own.principality.towns.findIndex((town) => town.town_id === command.town_id);
    const town = townIndex >= 0 ? `dorp ${townIndex + 1}` : "het gekozen dorp";
    return {
      eyebrow: "Bouwactie bevestigen",
      title: "Stad bouwen?",
      description: `Je verbouwt ${town} tot een stad. Controleer de plek en betaling voordat je bouwt.`,
      confirmLabel: "Stad bouwen",
    };
  }
  if (command.type === "build_card" || command.type === "build_open_card") {
    const instance = [...(own.hand || []), ...game.supply.open_cards]
      .find((card) => card.instance_id === command.instance_id);
    const definition = instance ? definitions.get(instance.card_id) : undefined;
    const name = definition?.name || "Geselecteerde kaart";
    const source = command.type === "build_open_card" ? "uit de open voorraad" : "uit je hand";
    if (definition?.id === "progress.raadhuis" && command.location.kind === "town") {
      const location = command.location;
      const townIndex = own.principality.towns.findIndex((town) => town.town_id === location.town_id);
      const town = townIndex >= 0 ? own.principality.towns[townIndex] : undefined;
      const replaced = town?.slots[location.slot];
      const replacedDefinition = replaced ? definitions.get(replaced.instance.card_id) : undefined;
      const replacedName = replacedDefinition?.name || "Parochiehuis";
      return {
        eyebrow: "Vervanging bevestigen",
        title: `${replacedName} door Raadhuis vervangen?`,
        description: `Je bouwt Raadhuis ${source} ${describeBuildLocation(location, own)}. Daarmee verdwijnt ${replacedName} van die bouwplaats en vervalt de werking ervan. Controleer de plek en betaling voordat je vervangt.`,
        confirmLabel: "Vervangen door Raadhuis",
      };
    }
    return {
      eyebrow: "Bouwactie bevestigen",
      title: `${name} bouwen?`,
      description: `Je bouwt ${name} ${source} ${describeBuildLocation(command.location, own)}. Controleer de plek en betaling voordat je bouwt.`,
      confirmLabel: `${name} bouwen`,
    };
  }
  if (command.type !== "remove_own_card") return null;
  const placed = allPlaced(own).find((candidate) => candidate.instance.instance_id === command.instance_id);
  const definition = placed ? definitions.get(placed.instance.card_id) : undefined;
  const name = definition?.name || "Geselecteerde uitbouw";
  const destination = definition?.open_in_theme ? "terug naar de open voorraad" : "naar de aflegstapel";
  return {
    eyebrow: "Verwijdering bevestigen",
    title: `${name} verwijderen?`,
    description: `${name} gaat ${destination}; de werking en eventuele punten verdwijnen direct uit je vorstendom.`,
    confirmLabel: `${name} definitief verwijderen`,
    danger: true,
  };
}

function describeBuildLocation(location: BuildLocation, own: PlayerView): string {
  if (location.kind === "region") {
    const upper = own.principality.upper_regions.find((region) => region.region_id === location.region_id);
    const lower = own.principality.lower_regions.find((region) => region.region_id === location.region_id);
    const region = upper || lower;
    if (!region) return "op het gekozen landschap";
    return `op ${resourceMeta[region.resource].label} met worp ${region.production_number} in de ${upper ? "bovenste" : "onderste"} rij`;
  }
  const townIndex = own.principality.towns.findIndex((town) => town.town_id === location.town_id);
  const town = townIndex >= 0 ? own.principality.towns[townIndex] : undefined;
  const direction = location.slot < 2 ? "boven" : "onder";
  const extra = location.slot === 1 || location.slot === 3 ? "extra " : "";
  const townLabel = townIndex >= 0 ? `${town?.kind || "dorp"} ${townIndex + 1}` : "de gekozen plaats";
  return `op de ${extra}bouwplaats ${direction} ${townLabel}`;
}

function CommandConfirmationDialog({ copy, busy, onCancel, onConfirm }: {
  copy: DirectCommandConfirmation;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="modal-layer priority-modal command-confirmation-layer">
      <section className="modal small-modal command-confirmation-dock" role="dialog" aria-modal="true" aria-labelledby="command-confirmation-title">
        <span className="eyebrow">{copy.eyebrow}</span>
        <h2 id="command-confirmation-title">{copy.title}</h2>
        <p>{copy.description}</p>
        <div className="modal-actions">
          <button type="button" autoFocus onClick={onCancel}>Annuleren</button>
          <button type="button" className={copy.danger ? "danger" : "primary"} disabled={busy} onClick={onConfirm}>{copy.confirmLabel}</button>
        </div>
      </section>
    </div>
  );
}

function commandResourcePlan(
  command: Command,
  game: GameView,
  own: PlayerView,
  definitions: Map<string, CardDef>,
): ResourcePlanRequest | null {
  const request = (title: string, payment: Resource[] = [], storage: Resource[] = [], requireStorage = false): ResourcePlanRequest => ({
    title,
    payment,
    storage,
    requireStorage,
  });
  if (command.type === "build_road") return request("Straat bouwen", ["hout", "baksteen", "baksteen"]);
  if (command.type === "build_village") return request("Dorp bouwen", ["hout", "baksteen", "graan", "wol"]);
  if (command.type === "upgrade_city") return request("Stad bouwen", ["graan", "graan", "erts", "erts", "erts"]);
  if (command.type === "build_card" || command.type === "build_open_card") {
    const instance = [...(own.hand || []), ...game.supply.open_cards].find((card) => card.instance_id === command.instance_id);
    const definition = instance ? definitions.get(instance.card_id) : undefined;
    if (!definition) return null;
    const values = [...definition.cost];
    if (command.discount_resource && buildDiscountAllowed(own, definition)) {
      const index = RESOURCES.indexOf(command.discount_resource);
      if (index >= 0 && values[index] > 0) values[index] -= 1;
    }
    return request(`${definition.name} bouwen`, costResources(values));
  }
  if (command.type === "trade") {
    const payment = command.count === 2 && command.source_region_id ? [] : Array(command.count).fill(command.give);
    return request("Grondstoffen ruilen", payment, [command.receive], true);
  }
  if (command.type === "begin_search_swap") {
    return request("Gericht zoeken betalen", command.payment);
  }
  if (command.type !== "play_action") return null;
  const instance = own.hand?.find((card) => card.instance_id === command.instance_id);
  const id = instance?.card_id;
  const params = command.params;
  const resources = params.resources || [];
  const targets = params.target_resources || [];
  if (id === "base.goudsmid" || id === "gold.goudsmid") return request("Goudsmid", ["goud", "goud", "goud"], resources);
  if (id === "base.karavaan") return request("Karavaan", targets, resources);
  if (["gold.handelsmeester", "gold.handelaar", "gold.rooftocht", "conflict.rooftocht", "gold.dounya", "conflict.roofvaart"].includes(id || "")) return null;
  if (id === "progress.drieslagstelsel") {
    return request("Drieslagstelsel", [], resources);
  }
  if (id === "progress.mijnbouw") {
    return request("Mijnbouw", [], resources);
  }
  return null;
}

function costResources(cost: number[]): Resource[] {
  return RESOURCES.flatMap((resource, index) => Array(cost[index] || 0).fill(resource));
}

function largeTradeShipRegionIds(player: PlayerView): Set<number> {
  const ids = new Set<number>();
  player.principality.towns.forEach((town, townIndex) => {
    town.slots.forEach((placed, slot) => {
      if (!placed || !["base.groot_handelsschip", "gold.groot_handelsschip"].includes(placed.instance.card_id)) return;
      const row = slot < 2 ? player.principality.upper_regions : player.principality.lower_regions;
      ids.add(row[townIndex].region_id);
      ids.add(row[townIndex + 1].region_id);
    });
  });
  return ids;
}

function buildDiscountAllowed(own: PlayerView, definition: CardDef): boolean {
  const ids = allPlaced(own).map((placed) => placed.instance.card_id);
  return (definition.unit_kind === "held" && ids.includes("conflict.oefenterrein"))
    || (definition.kind === "stadsgebouw"
      && definition.cost.reduce((total, count) => total + count, 0) > 4
      && ids.includes("progress.bouwkraan"));
}

function turnHint(game: GameView, isTurn: boolean): string {
  if (game.status === "wachten_op_tegenstander") return "De uitnodiging is klaar. Zodra de ander aansluit, start de voorbereiding.";
  if (game.status === "gepauzeerd") return "Geen acties worden uitgevoerd totdat iemand het spel hervat.";
  if (game.status === "afgelopen") return `${game.players[game.winner === "rood" ? 0 : 1].name} heeft het duel gewonnen.`;
  if (!isTurn && game.status === "actief") return `${game.players[game.turn.active === "rood" ? 0 : 1].name} is aan de beurt.`;
  return "Jij bent aan zet.";
}

function ActionCardDialog({ game, own, opponent, instance, definition, definitions, busy, onCancel, onSubmit }: {
  game: GameView; own: PlayerView; opponent: PlayerView; instance: CardInstance; definition: CardDef; definitions: Map<string, CardDef>; busy: boolean;
  onCancel: () => void; onSubmit: (params: CardParams) => void;
}) {
  const [resources, setResources] = useState<Resource[]>([]);
  const [targetResources, setTargetResources] = useState<Resource[]>([]);
  const [targetIds, setTargetIds] = useState<number[]>([]);
  const [regionIds, setRegionIds] = useState<number[]>([]);
  const [townId, setTownId] = useState<number>();
  const [roll, setRoll] = useState(1);
  const [moveMode, setMoveMode] = useState<"regions" | "placed_cards">("regions");
  const id = definition.id;
  const resourceCounts = actionResourceCounts(id, game);
  const placedTargets: ReturnType<typeof allPlaced> = [];
  const needsRegions = id === "base.verhuizing" || id === "progress.verhuizing";
  const needsTown = id === "progress.medicus";
  const noParams = ["gold.reiner", "conflict.boogschutter", "conflict.pyromaan", "conflict.verrader", "progress.bob", "progress.guido", "progress.theo"].includes(id);

  const toggle = <T,>(list: T[], value: T, limit: number, setter: (value: T[]) => void) => {
    if (list.includes(value)) setter(list.filter((item) => item !== value));
    else if (list.length < limit) setter([...list, value]);
  };
  const addResource = (list: Resource[], value: Resource, limit: number, setter: (value: Resource[]) => void) => {
    if (list.length < limit) setter([...list, value]);
  };
  const removeResource = (list: Resource[], value: Resource, setter: (value: Resource[]) => void) => {
    const index = list.lastIndexOf(value);
    if (index >= 0) setter(list.filter((_, item) => item !== index));
  };
  const targetLimit = (resource: Resource) => {
    if (id === "gold.rooftocht" || id === "conflict.rooftocht") {
      const singleCardCapacity = Math.max(0, ...resourceLocations(own)
        .filter((option) => option.resource === resource)
        .map((option) => option.capacity));
      return Math.min(3, resourceTotal(opponent, resource, "payment"), singleCardCapacity);
    }
    return resourceCounts.target || 0;
  };
  const canAddTargetResource = (resource: Resource) => {
    const sameType = id === "gold.rooftocht" || id === "conflict.rooftocht" || id === "gold.dounya";
    const limit = Math.min(resourceCounts.target || 0, targetLimit(resource));
    return limit > 0 && ((sameType && targetResources.length > 0 && targetResources[0] !== resource)
      || targetResources.length < limit);
  };
  const addTargetResource = (resource: Resource) => {
    const sameType = id === "gold.rooftocht" || id === "conflict.rooftocht" || id === "gold.dounya";
    const limit = Math.min(resourceCounts.target || 0, targetLimit(resource));
    if (sameType && targetResources.length && targetResources[0] !== resource) {
      if (limit > 0) setTargetResources([resource]);
      return;
    }
    if (targetResources.length < limit) {
      setTargetResources([...targetResources, resource]);
      return;
    }
  };
  const removeTargetResource = (resource: Resource) => removeResource(targetResources, resource, setTargetResources);
  const regionOptions = [...own.principality.upper_regions, ...own.principality.lower_regions].map((region) => region.region_id);
  const placedOptions = movablePlacedIds(own, definitions, targetIds);
  useTargetBoardPicker({
    enabled: needsRegions,
    kind: moveMode,
    title: moveMode === "regions" ? "Kies twee landschappen" : "Kies twee uitbouwkaarten",
    seat: own.seat,
    selectedIds: moveMode === "regions" ? regionIds : targetIds,
    allowedIds: moveMode === "regions" ? regionOptions : placedOptions,
    limit: 2,
    onToggle: (target) => {
      if (moveMode === "regions") {
        setTargetIds([]);
        toggle(regionIds, target, 2, setRegionIds);
      } else {
        setRegionIds([]);
        toggle(targetIds, target, 2, setTargetIds);
      }
    },
  });
  const valid =
    (id === "base.marileen" || id === "progress.marileen") ? true :
    resourceCounts.own !== undefined && (resources.length < (resourceCounts.ownMin ?? resourceCounts.own) || resources.length > resourceCounts.own) ? false :
    resourceCounts.target !== undefined && (targetResources.length < (resourceCounts.targetMin ?? resourceCounts.target) || targetResources.length > resourceCounts.target) ? false :
    needsRegions && regionIds.length !== 2 && targetIds.length !== 2 ? false :
    placedTargets.length && targetIds.length !== 1 ? false :
    needsTown && townId === undefined ? false : true;

  return (
    <div className={`modal-layer ${needsRegions ? "board-interaction-layer board-safe-dock-layer relocation-board-dock-layer" : ""}`}><section className={`modal card-command-modal ${needsRegions ? "board-interaction-dock board-safe-dock relocation-board-dock" : ""}`} role="dialog" aria-modal={needsRegions ? "false" : "true"}>
      <GameCard instance={instance} definition={definition} />
      <div className="command-form"><span className="eyebrow">Actiekaart spelen</span><h2>{definition.name}</h2><p>{definition.text}</p>
        {(id === "base.marileen" || id === "progress.marileen") ? <label>Kies de opbrengst<select value={roll} onChange={(event) => setRoll(Number(event.target.value))}>{[1,2,3,4,5,6].map((number) => <option key={number}>{number}</option>)}</select></label> : null}
        {resourceCounts.own !== undefined ? <ResourceMulti title={id === "base.karavaan" ? "Ontvang" : "Kies grondstoffen"} selected={resources} count={resourceCounts.own} allowed={id === "progress.drieslagstelsel" ? ["graan"] : id === "progress.mijnbouw" ? ["erts"] : undefined} onAdd={(resource) => addResource(resources, resource, resourceCounts.own!, setResources)} onRemove={(resource) => removeResource(resources, resource, setResources)} onClear={() => setResources([])} /> : null}
        {resourceCounts.target !== undefined ? <ResourceMulti title={id === "base.karavaan" ? "Geef af" : "Neem van de ander"} selected={targetResources} count={resourceCounts.target} allowed={id === "gold.dounya" ? ["goud"] : undefined} canAdd={canAddTargetResource} onAdd={addTargetResource} onRemove={removeTargetResource} onClear={() => setTargetResources([])} /> : null}
        {needsRegions ? <div className="board-target-picker"><strong>Wat wil je verwisselen?</strong><div><button type="button" className={moveMode === "regions" ? "selected" : ""} onClick={() => { setMoveMode("regions"); setTargetIds([]); }}>Landschappen</button><button type="button" className={moveMode === "placed_cards" ? "selected" : ""} onClick={() => { setMoveMode("placed_cards"); setRegionIds([]); }}>Uitbouwkaarten</button></div><p>Klik rechtstreeks op twee goud gemarkeerde kaarten op je bord. Je mag een landschap uit de startopstelling met een nieuw landschap verwisselen. Gekozen: {moveMode === "regions" ? regionIds.length : targetIds.length}/2.</p>{regionIds.length || targetIds.length ? <button type="button" className="selection-clear" onClick={() => { setRegionIds([]); setTargetIds([]); }}>Selectie wissen</button> : null}</div> : null}
        {placedTargets.length ? <div className="target-list"><strong>Kies doelwit</strong>{placedTargets.map((placed) => <button className={targetIds.includes(placed.instance.instance_id) ? "selected" : ""} key={placed.instance.instance_id} onClick={() => setTargetIds(targetIds.includes(placed.instance.instance_id) ? [] : [placed.instance.instance_id])}>{definitions.get(placed.instance.card_id)?.name}</button>)}</div> : null}
        {needsTown ? <label>Kies een stad met Badhuis<select value={townId || ""} onChange={(event) => setTownId(Number(event.target.value))}><option value="">Kies…</option>{own.principality.towns.filter((town) => town.slots.some((placed) => placed?.instance.card_id === "progress.badhuis")).map((town, index) => <option key={town.town_id} value={town.town_id}>Stad {index + 1}</option>)}</select></label> : null}
        {id === "conflict.rob" ? <p className="warning-note">Rob wordt automatisch aangeboden zodra Broedertwist, Vete of Oproer jou treft.</p> : null}
        {id === "base.verkenner" ? <p className="warning-note"><strong>Hierna is de dorpbouw verplicht.</strong> Je kiest eerst links of rechts op het bord en betaalt het dorp; daarna bepaal je uit de volledige stapel welk landschap boven en welk landschap onder komt.</p> : null}
        {noParams ? <p className="success-note">Deze kaart heeft geen extra keuze nodig.</p> : null}
        <div className="modal-actions"><button onClick={onCancel}>Annuleren</button><button className="primary" disabled={!valid || busy || id === "conflict.rob"} onClick={() => onSubmit({ resources, target_resources: targetResources, target_instance_ids: targetIds, target_region_ids: regionIds, target_town_id: townId, production_roll: roll })}>{id === "base.verkenner" ? "Verkenner spelen en dorpbouw starten" : "Kaart spelen"}</button></div>
      </div>
    </section></div>
  );
}

function actionResourceCounts(id: string, game: GameView): { own?: number; ownMin?: number; target?: number; targetMin?: number } {
  if (id === "base.goudsmid" || id === "gold.goudsmid") return { own: 2 };
  if (id === "base.karavaan") return { own: 2, target: 2 };
  if (id === "gold.handelsmeester") return { target: 2, targetMin: 1 };
  if (id === "gold.handelaar") return { own: 1, target: 2, targetMin: 1 };
  if (id === "gold.rooftocht" || id === "conflict.rooftocht") return { target: 3, targetMin: 1 };
  if (id === "progress.drieslagstelsel" || id === "progress.mijnbouw") return { own: 2, ownMin: 1 };
  if (id === "gold.dounya") {
    const own = game.players[game.viewer === "rood" ? 0 : 1];
    const opponent = game.players[game.viewer === "rood" ? 1 : 0];
    const pirates = allPlaced(own).filter((placed) => placed.instance.card_id === "gold.piratenschip").length;
    return {
      target: Math.min(
        pirates * 2,
        resourceTotal(opponent, "goud", "payment"),
        resourceTotal(own, "goud", "storage"),
      ),
      targetMin: 1,
    };
  }
  if (id === "conflict.roofvaart") {
    const me = game.scores[game.viewer === "rood" ? 0 : 1].victory;
    const other = game.scores[game.viewer === "rood" ? 1 : 0].victory;
    return { target: other > me ? 2 : 1, targetMin: 1 };
  }
  return {};
}

type MovableLocation =
  | { kind: "region" }
  | { kind: "town"; townKind: "dorp" | "stad"; slot: number };

function movablePlacedIds(player: PlayerView, definitions: Map<string, CardDef>, selected: number[]): number[] {
  const placed = allPlaced(player);
  if (!selected.length) return placed.map((card) => card.instance.instance_id);
  const first = placed.find((card) => card.instance.instance_id === selected[0]);
  const firstLocation = first ? movableLocation(player, first.instance.instance_id) : undefined;
  const firstDefinition = first ? definitions.get(first.instance.card_id) : undefined;
  if (!first || !firstLocation || !firstDefinition) return [];
  return placed.flatMap((candidate) => {
    if (candidate.instance.instance_id === first.instance.instance_id) return [candidate.instance.instance_id];
    const candidateLocation = movableLocation(player, candidate.instance.instance_id);
    const candidateDefinition = definitions.get(candidate.instance.card_id);
    return candidateLocation
      && candidateDefinition
      && locationAcceptsDefinition(candidateLocation, firstDefinition)
      && locationAcceptsDefinition(firstLocation, candidateDefinition)
      ? [candidate.instance.instance_id]
      : [];
  });
}

function movableLocation(player: PlayerView, instanceId: number): MovableLocation | undefined {
  for (const town of player.principality.towns) {
    const slot = town.slots.findIndex((placed) => placed?.instance.instance_id === instanceId);
    if (slot >= 0) return { kind: "town", townKind: town.kind, slot };
  }
  const region = [...player.principality.upper_regions, ...player.principality.lower_regions]
    .find((candidate) => candidate.expansion?.instance.instance_id === instanceId);
  return region ? { kind: "region" } : undefined;
}

function locationAcceptsDefinition(location: MovableLocation, definition: CardDef): boolean {
  if (location.kind === "region") return definition.kind === "landschapsuitbouw";
  if (!["nederzettingsgebouw", "nederzettingseenheid", "stadsgebouw"].includes(definition.kind)) return false;
  return (definition.kind !== "stadsgebouw" || location.townKind === "stad")
    && (location.townKind === "stad" || location.slot === 0 || location.slot === 2);
}

function ResourceMulti({ title, selected, count, allowed = RESOURCES, canAdd, onAdd, onRemove, onClear }: {
  title: string;
  selected: Resource[];
  count: number;
  allowed?: Resource[];
  canAdd?: (resource: Resource) => boolean;
  onAdd: (resource: Resource) => void;
  onRemove: (resource: Resource) => void;
  onClear: () => void;
}) {
  return <fieldset className="resource-multi"><legend>{title} ({selected.length}/{count})</legend><ResourceCountSelector selected={selected} count={count} allowed={allowed} canAdd={canAdd} onAdd={onAdd} onRemove={onRemove} onClear={onClear} /></fieldset>;
}

function DirectedSearchPayment({ own, selected, setSelected, onSearch, busy, disabled }: {
  own: PlayerView;
  selected: Resource[];
  setSelected: (resources: Resource[]) => void;
  onSearch: () => void;
  busy: boolean;
  disabled: boolean;
}) {
  const ids = allPlaced(own).map((placed) => placed.instance.card_id);
  const cost = ids.includes("progress.raadhuis") ? 0 : ids.includes("base.parochiehuis") ? 1 : 2;
  const add = (resource: Resource) => {
    if (selected.length < cost) setSelected([...selected, resource]);
  };
  const remove = (resource: Resource) => {
    const index = selected.lastIndexOf(resource);
    if (index >= 0) setSelected(selected.filter((_, item) => item !== index));
  };
  return <div className="directed-search"><strong>Gericht zoeken ({cost} grondstof{cost === 1 ? "" : "fen"})</strong><p>Kies daarna één kaart uit de doelstapel. De kaarten verschijnen met de <b>bovenste kaart eerst</b> en een label per stapelpositie.</p>{selected.length ? <ResourceAmounts resources={selected} compact className="directed-search-summary" /> : null}{cost ? <ResourceCountSelector className="resource-payments" selected={selected} count={cost} disabled={busy || disabled} compact onAdd={add} onRemove={remove} onClear={() => setSelected([])} /> : null}<button disabled={busy || disabled || selected.length !== cost} onClick={onSearch}>Doorzoek gekozen stapel</button></div>;
}
